import { useSWRImmutable } from "@/hooks/swr/immutable";
import { safeLocalMutate } from "@/hooks/swr/swr";
import { useCurrentUser } from "@/hooks/user/useCurrentUser";
import { callListFollowers, callListFollowing, follow, isFollowingUser, unfollow } from "@/utils/followUtils";
import { User } from "@knowt/syncing/graphql/schema";
import { useCallback } from "react";
import { mutate } from "swr";
import { handleMaybeProgressing } from "../gamification/monitoring/progressing";

export const useCurrentUserFollowUnfollow = ({
    userId: userIdToFollow,
    enabled = true,
    initialFollowState = false,
}: {
    userId: string;
    enabled?: boolean;
    initialFollowState?: boolean;
}) => {
    const { userId, user } = useCurrentUser();

    const { data: isFollowing, mutate: mutateIsFollowing } = useSWRImmutable(
        userId && ["isCurrentUserFollowUser", userId, userIdToFollow],
        async () => (enabled ? await isFollowingUser(userIdToFollow) : initialFollowState)
    );

    const toggleFollow = useCallback(async () => {
        const wasFollowing = isFollowing;
        if (wasFollowing === undefined || !userIdToFollow) return;

        // const maybeV2User = await toggleFollowing(isFollowing, userIdToFollow);

        if (wasFollowing) {
            await unfollow(userIdToFollow);
        } else {
            const v2Response = await follow(userIdToFollow);
            await handleMaybeProgressing(v2Response);
        }

        await mutateIsFollowing(!wasFollowing, { revalidate: false });

        await mutate(["followingList", userId], async currentCache => {
            if (wasFollowing) {
                return currentCache.filter(u => (u.ID || u.userId) !== userIdToFollow);
            } else {
                return [...currentCache, user];
            }
        });

        await safeLocalMutate(["followersList", userIdToFollow], async currentCache => {
            if (wasFollowing) {
                return currentCache.filter(u => (u.ID || u.userId) !== userId);
            } else {
                return [...currentCache, user];
            }
        });
    }, [isFollowing, mutateIsFollowing, user, userId, userIdToFollow, enabled]);

    return { isFollowing, toggleFollow };
};

export const useUserFollowingFollowers = ({
    userId,
}: {
    userId: string;
}): { following: User[]; followers: User[]; isValidating: boolean } => {
    const { userId: currentUserId } = useCurrentUser();

    const { data: _following, isValidating: isValidatingFollowing } = useSWRImmutable(
        currentUserId && userId && ["followingList", userId],
        () => {
            return callListFollowing({ userId, extraDetails: true });
        }
    );

    const { data: _followers, isValidating: isValidatingFollowers } = useSWRImmutable(
        currentUserId && userId && ["followersList", userId],
        () => {
            return callListFollowers({ userId, extraDetails: true });
        }
    );

    return {
        following: _following || [],
        followers: _followers || [],
        isValidating: isValidatingFollowing || isValidatingFollowers,
    };
};
